import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Modal, Form, Alert, Spinner } from 'react-bootstrap';
import {
  Lock,
  Envelope,
  Eye,
  EyeSlash,
  CheckCircle,
  XCircle,
  PersonFill,
  CalendarFill,
} from 'react-bootstrap-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDebounce } from '../../../hooks/useDebounce';
import { useAuth } from '../../../contexts/AuthContext';
import './LoginModal.css';
import { LoginModalProps, PasswordValidation } from '../../../types/loginModal';
import {
  isPasswordValid,
  validatePassword,
} from '../../../utils/passwordvalidation';
import { authService } from '../../../services';
import { ApiError } from '../../../services/api.client';

type ModalView =
  | 'login'
  | 'register'
  | 'confirm-email'
  | 'free-access'
  | 'forgot-password'
  | 'forgot-sent';

const INITIAL_VALIDATION: PasswordValidation = {
  hasMinLength: false,
  hasUpperCase: false,
  hasLowerCase: false,
  hasNumber: false,
  hasSymbol: false,
};

const strengthScore = (v: PasswordValidation) =>
  Object.values(v).filter(Boolean).length;

const STRENGTH_COLORS = [
  'var(--accent)',
  'var(--secondary)',
  'var(--primary)',
  'var(--tertiary-light)',
  'var(--tertiary)',
];
const STRENGTH_LABELS = [
  '',
  'Molto debole',
  'Debole',
  'Discreta',
  'Buona',
  'Ottima',
];

const ValidationItem = ({ valid, text }: { valid: boolean; text: string }) => (
  <div className="d-flex align-items-center gap-2 mb-1">
    {valid ? (
      <CheckCircle
        size={13}
        className="lm-validation-icon-valid flex-shrink-0"
      />
    ) : (
      <XCircle size={13} className="lm-validation-icon-invalid flex-shrink-0" />
    )}
    <span
      className={`small ${valid ? 'lm-validation-valid' : 'lm-validation-invalid'}`}
    >
      {text}
    </span>
  </div>
);

const StrengthBar = ({ validation }: { validation: PasswordValidation }) => {
  const score = strengthScore(validation);
  const color = STRENGTH_COLORS[score - 1] ?? 'var(--border-body)';
  const label = STRENGTH_LABELS[score];
  return (
    <div className="mb-2">
      <div className="lm-strength-bar">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="lm-strength-segment"
            style={i <= score ? { backgroundColor: color } : undefined}
          />
        ))}
      </div>
      {!isPasswordValid(validation) && label && (
        <p className="small fw-medium mb-0 mt-1" style={{ color }}>
          {label}
        </p>
      )}
    </div>
  );
};

const LoginModal: React.FC<LoginModalProps> = ({
  show,
  onHide,
  onLoginSuccess,
  disableRedirect = false,
  showFreeAccessTab = false,
  initialView,
  lockEmail,
}) => {
  const {
    login,
    loginWithSocial,
    isLoading: authLoading,
    isAuthenticated,
    error: authError,
    clearError,
    user,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingNavigation = useRef<string | null>(null);

  const defaultView: ModalView =
    initialView ?? (showFreeAccessTab ? 'free-access' : 'login');
  const [view, setView] = useState<ModalView>(defaultView);
  const [flipped, setFlipped] = useState(defaultView === 'register');

  // login
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [rememberMe, setRememberMe] = useState(true);

  // register
  const [regName, setRegName] = useState('');
  const [regDob, setRegDob] = useState('');
  const [regEmail, setRegEmail] = useState(lockEmail ?? '');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [pwdBlurred, setPwdBlurred] = useState(false);
  const [confirmBlurred, setConfirmBlurred] = useState(false);
  const [pwdValidation, setPwdValidation] =
    useState<PasswordValidation>(INITIAL_VALIDATION);
  const debouncedPwd = useDebounce(regPassword, 600);

  // free-access
  const [freeEmail, setFreeEmail] = useState('');
  const [freeLoading, setFreeLoading] = useState(false);
  const [freeError, setFreeError] = useState('');
  const [freeSent, setFreeSent] = useState(false);

  // forgot-password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // social login
  const [socialLoading, setSocialLoading] = useState<
    'google' | 'facebook' | null
  >(null);
  const [socialError, setSocialError] = useState('');

  useEffect(() => {
    setPwdValidation(
      regPassword ? validatePassword(regPassword) : INITIAL_VALIDATION
    );
  }, [regPassword]);

  useEffect(() => {
    if (authError) {
      setLoginError(authError);
      setFailedAttempts((prev) => prev + 1);
    }
  }, [authError]);

  const showPwdHints =
    pwdBlurred && debouncedPwd.length > 0 && !isPasswordValid(pwdValidation);
  const passwordsMatch = regPassword === regConfirm;
  const showConfirmError =
    confirmBlurred && regConfirm.length > 0 && !passwordsMatch;

  const resetAll = useCallback(() => {
    setView(defaultView);
    setFlipped(defaultView === 'register');
    setLoginEmail('');
    setLoginPassword('');
    setShowLoginPwd(false);
    setLoginError('');
    setFailedAttempts(0);
    setRememberMe(true);
    setRegName('');
    setRegDob('');
    setRegEmail(lockEmail ?? '');
    setRegPassword('');
    setRegConfirm('');
    setShowRegPwd(false);
    setShowRegConfirm(false);
    setRegError('');
    setPwdBlurred(false);
    setConfirmBlurred(false);
    setPwdValidation(INITIAL_VALIDATION);
    setFreeEmail('');
    setFreeError('');
    setFreeSent(false);
    setForgotEmail('');
    setForgotError('');
    setSocialLoading(null);
    setSocialError('');
    pendingNavigation.current = null;
    clearError();
  }, [clearError, defaultView, lockEmail]);

  useEffect(() => {
    if (!show) resetAll();
  }, [show, resetAll]);

  const isBackFace = view === 'register' || view === 'confirm-email';

  const goToFront = () => {
    setFlipped(false);
    setView(defaultView === 'register' ? 'login' : defaultView);
  };

  const goToRegister = () => {
    setView('register');
    setFlipped(true);
  };

  const goToLogin = () => {
    setView('login');
    setFlipped(false);
  };

  const goToForgot = () => {
    // forgot-password rimane nella front face — non flippa
    setForgotEmail(loginEmail); // pre-popola con l'email già inserita
    setForgotError('');
    setView('forgot-password');
    setFlipped(false);
  };

  const headerMap: Record<
    ModalView,
    { icon: string; title: string; sub: string }
  > = {
    login: { icon: '🔐', title: 'Bentornato!', sub: 'Accedi al tuo account' },
    register: {
      icon: '✨',
      title: 'Crea il tuo account',
      sub: 'Registrati gratuitamente',
    },
    'confirm-email': { icon: '📬', title: 'Controlla la tua email', sub: '' },
    'free-access': {
      icon: '🎁',
      title: 'Accedi alle risorse gratuite',
      sub: 'Inserisci la tua email per ricevere il link di accesso',
    },
    'forgot-password': {
      icon: '🔑',
      title: 'Password dimenticata?',
      sub: 'Ti inviamo il link per reimpostarla',
    },
    'forgot-sent': {
      icon: '📬',
      title: 'Controlla la tua email',
      sub: '',
    },
  };
  const { icon, title, sub } = headerMap[view];

  // ── Login submit ──────────────────────────────────────────────
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    clearError();

    if (!loginEmail.trim()) {
      setLoginError('Inserisci la tua email.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(loginEmail.trim())) {
      setLoginError('Inserisci un indirizzo email valido.');
      return;
    }
    if (!loginPassword) {
      setLoginError('Inserisci la tua password.');
      return;
    }

    const loggedUser = await login({
      email: loginEmail.trim(),
      password: loginPassword,
      rememberMe,
    });
    if (!loggedUser) return;

    onLoginSuccess?.(loggedUser);

    if (!disableRedirect) {
      const roleRedirect: Record<string, string> = {
        superadmin: '/superadmin',
        admin: '/admin',
        user: '/dashboard',
      };
      const from = (location.state as { from?: { pathname: string } })?.from
        ?.pathname;
      pendingNavigation.current =
        from ?? roleRedirect[loggedUser.role] ?? '/dashboard';
    }

    onHide();
  };

  // ── Register submit ───────────────────────────────────────────
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError('');

    if (!regName.trim()) {
      setRegError('Inserisci il tuo nome e cognome.');
      return;
    }
    if (!regDob) {
      setRegError('Inserisci la tua data di nascita.');
      return;
    }
    if (!regEmail.trim()) {
      setRegError('Inserisci la tua email.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(regEmail.trim())) {
      setRegError('Inserisci un indirizzo email valido.');
      return;
    }
    if (!regPassword) {
      setRegError('Inserisci una password.');
      return;
    }
    if (!isPasswordValid(validatePassword(regPassword))) {
      setPwdBlurred(true);
      setRegError('La password non rispetta i requisiti di sicurezza.');
      return;
    }
    if (!regConfirm) {
      setRegError('Conferma la tua password.');
      return;
    }
    if (!passwordsMatch) {
      setConfirmBlurred(true);
      setRegError('Le password non coincidono.');
      return;
    }

    setRegLoading(true);
    try {
      await authService.register(
        regName.trim(),
        regDob,
        regEmail.trim(),
        regPassword
      );
      const loggedUser = await login({
        email: regEmail.trim(),
        password: regPassword,
      });
      if (loggedUser) {
        onLoginSuccess?.(loggedUser);
        if (!disableRedirect) {
          const roleRedirect: Record<string, string> = {
            superadmin: '/superadmin',
            admin: '/admin',
            user: '/dashboard',
          };
          pendingNavigation.current =
            roleRedirect[loggedUser.role] ?? '/dashboard';
        }
        setView('confirm-email');
      }
    } catch (err) {
      setRegError(
        err instanceof Error
          ? err.message
          : 'Errore durante la registrazione. Riprova più tardi.'
      );
    } finally {
      setRegLoading(false);
    }
  };

  // ── Free access submit ────────────────────────────────────────
  const handleFreeAccessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFreeError('');

    if (!freeEmail.trim()) {
      setFreeError('Inserisci la tua email.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(freeEmail.trim())) {
      setFreeError('Inserisci un indirizzo email valido.');
      return;
    }

    setFreeLoading(true);
    try {
      await authService.registerFree(freeEmail.trim());
      setFreeSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        // Email già registrata: se è un account free che ha perso il primo
        // link, gliene mandiamo uno nuovo (il backend non rivela se l'email
        // esiste davvero, coerente col resto del flusso "forgot password").
        try {
          await authService.sendFreeLoginLink(freeEmail.trim());
          setFreeSent(true);
        } catch (retryErr) {
          setFreeError(
            retryErr instanceof Error
              ? retryErr.message
              : "Errore durante l'invio."
          );
        }
      } else {
        setFreeError(
          err instanceof Error ? err.message : "Errore durante l'invio."
        );
      }
    } finally {
      setFreeLoading(false);
    }
  };

  // ── Forgot password submit ────────────────────────────────────
  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');

    if (!forgotEmail.trim()) {
      setForgotError('Inserisci la tua email.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(forgotEmail.trim())) {
      setForgotError('Inserisci un indirizzo email valido.');
      return;
    }

    setForgotLoading(true);
    try {
      await authService.sendPasswordResetEmail(forgotEmail.trim());
      setView('forgot-sent');
    } catch {
      // Il backend risponde sempre 200, quindi qui non dovremmo mai arrivare
      // salvo errori di rete
      setForgotError('Errore di rete. Riprova più tardi.');
    } finally {
      setForgotLoading(false);
    }
  };

  // ── Social login handler ──────────────────────────────────
  const handleSocialLogin = async (provider: 'google' | 'facebook') => {
    setSocialError('');
    setSocialLoading(provider);
    try {
      const loggedUser = await loginWithSocial(provider);
      if (!loggedUser) return; // popup chiuso senza completare

      onLoginSuccess?.(loggedUser);

      if (!disableRedirect) {
        const roleRedirect: Record<string, string> = {
          superadmin: '/superadmin',
          admin: '/admin',
          user: '/dashboard',
        };
        const from = (location.state as { from?: { pathname: string } })?.from
          ?.pathname;
        pendingNavigation.current =
          from ?? roleRedirect[loggedUser.role] ?? '/dashboard';
      }

      onHide();
    } catch (err) {
      setSocialError(
        err instanceof Error ? err.message : 'Errore durante il login social.'
      );
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="lm-modal"
      backdrop="static"
      onExited={() => {
        if (pendingNavigation.current && !disableRedirect) {
          navigate(pendingNavigation.current, { replace: true });
          pendingNavigation.current = null;
        }
      }}
    >
      <Modal.Header closeButton>
        <div className="text-center w-100">
          <div className="fs-2 mb-2">{icon}</div>
          <h4 className="lm-header-title">{title}</h4>
          {sub && <p className="lm-header-subtitle mb-0">{sub}</p>}
        </div>
      </Modal.Header>

      <Modal.Body className="p-4">
        <div className="lm-flip-outer">
          <div className={`lm-flip-inner ${isBackFace ? 'flipped' : ''}`}>
            {/* ══ FRONT ══ */}
            <div className="lm-flip-face front">
              {/* Tab switcher */}
              {showFreeAccessTab &&
                (view === 'free-access' || view === 'login') && (
                  <div className="lm-tab-switcher mb-4">
                    <button
                      type="button"
                      className={`lm-tab-btn ${view === 'free-access' ? 'lm-tab-btn--active' : ''}`}
                      onClick={() => setView('free-access')}
                    >
                      🎁 Accesso gratuito
                    </button>
                    <button
                      type="button"
                      className={`lm-tab-btn ${view === 'login' ? 'lm-tab-btn--active' : ''}`}
                      onClick={() => setView('login')}
                    >
                      🔐 Accedi / Registrati
                    </button>
                  </div>
                )}

              {/* ── FREE ACCESS ── */}
              {view === 'free-access' && (
                <>
                  {freeSent ? (
                    <div className="text-center py-3">
                      <div className="fs-1 mb-3">📬</div>
                      <h5 className="fw-bold mb-2">Email inviata!</h5>
                      <p
                        className="small text-body-secondary mx-auto mb-4"
                        style={{ maxWidth: 300 }}
                      >
                        Controlla la casella di <strong>{freeEmail}</strong>.
                        Troverai il link per accedere alle risorse gratuite.
                      </p>
                      <button
                        type="button"
                        className="lm-btn-primary d-block mx-auto"
                        style={{ maxWidth: 220 }}
                        onClick={onHide}
                      >
                        Ho capito
                      </button>
                      <p className="text-center small text-body-secondary mt-3 mb-0">
                        Email sbagliata?{' '}
                        <button
                          type="button"
                          className="lm-switch-link"
                          onClick={() => {
                            setFreeSent(false);
                            setFreeEmail('');
                          }}
                        >
                          Cambia email
                        </button>
                      </p>
                    </div>
                  ) : (
                    <>
                      {freeError && (
                        <Alert
                          variant="danger"
                          dismissible
                          onClose={() => setFreeError('')}
                          className="small"
                        >
                          {freeError}
                        </Alert>
                      )}
                      <p className="small text-body-secondary mb-3">
                        Inserisci la tua email e ti mandiamo subito il link per
                        accedere a tutte le risorse gratuite di Fit Anatomy.
                        Nessuna password richiesta.
                      </p>
                      <Form onSubmit={handleFreeAccessSubmit} noValidate>
                        <Form.Label
                          className="small fw-semibold"
                          htmlFor="free-email"
                        >
                          Email
                        </Form.Label>
                        <div className="lm-input-wrap">
                          <span className="lm-input-icon">
                            <Envelope size={16} />
                          </span>
                          <Form.Control
                            id="free-email"
                            type="email"
                            placeholder="latua@email.it"
                            value={freeEmail}
                            onChange={(e) => setFreeEmail(e.target.value)}
                            autoComplete="email"
                          />
                        </div>
                        <button
                          type="submit"
                          className="lm-btn-primary mt-2"
                          disabled={freeLoading}
                        >
                          {freeLoading ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Invio in corso...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-send me-2" />
                              Inviami il link gratuito
                            </>
                          )}
                        </button>
                      </Form>
                      <p className="text-center small text-body-secondary mt-3 mb-0">
                        Vuoi anche acquistare i corsi?{' '}
                        <button
                          type="button"
                          className="lm-switch-link"
                          onClick={goToRegister}
                        >
                          Crea account completo
                        </button>
                      </p>
                    </>
                  )}
                </>
              )}

              {/* ── LOGIN ── */}
              {view === 'login' && (
                <>
                  {loginError && (
                    <Alert
                      variant="danger"
                      dismissible
                      onClose={() => {
                        setLoginError('');
                        clearError();
                      }}
                      className="small"
                    >
                      {loginError}
                    </Alert>
                  )}
                  {failedAttempts >= 3 && (
                    <div className="lm-failed-hint p-3 mb-3">
                      💡 Ricorda: la password deve contenere almeno 8 caratteri,
                      una maiuscola, un numero e un simbolo.
                    </div>
                  )}
                  <Form onSubmit={handleLoginSubmit} noValidate>
                    <Form.Label
                      className="small fw-semibold"
                      htmlFor="login-email"
                    >
                      Email
                    </Form.Label>
                    <div className="lm-input-wrap">
                      <span className="lm-input-icon">
                        <Envelope size={16} />
                      </span>
                      <Form.Control
                        id="login-email"
                        type="email"
                        placeholder="latua@email.it"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        autoComplete="email"
                      />
                    </div>
                    <Form.Label
                      className="small fw-semibold"
                      htmlFor="login-password"
                    >
                      Password
                    </Form.Label>
                    <div className="lm-input-wrap">
                      <span className="lm-input-icon">
                        <Lock size={16} />
                      </span>
                      <Form.Control
                        id="login-password"
                        type={showLoginPwd ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="lm-toggle"
                        onClick={() => setShowLoginPwd((v) => !v)}
                        aria-label="Mostra/nascondi password"
                      >
                        {showLoginPwd ? (
                          <EyeSlash size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <Form.Check
                        type="checkbox"
                        id="rememberMe"
                        label={<span className="small">Ricordami</span>}
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      {/* ← collegato a goToForgot */}
                      <button
                        type="button"
                        className="lm-forgot"
                        onClick={goToForgot}
                      >
                        Password dimenticata?
                      </button>
                    </div>
                    <button
                      type="submit"
                      className="lm-btn-primary"
                      disabled={authLoading}
                    >
                      {authLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Accesso in corso...
                        </>
                      ) : (
                        'Accedi'
                      )}
                    </button>
                  </Form>
                  <div className="lm-divider my-4">oppure</div>
                  {socialError && (
                    <Alert
                      variant="danger"
                      dismissible
                      onClose={() => setSocialError('')}
                      className="small"
                    >
                      {socialError}
                    </Alert>
                  )}
                  <div className="d-flex gap-2 mb-3">
                    <button
                      type="button"
                      className="lm-social-btn btn d-flex align-items-center justify-content-center gap-2 flex-fill"
                      onClick={() => handleSocialLogin('google')}
                      disabled={!!socialLoading}
                    >
                      {socialLoading === 'google' ? (
                        <Spinner as="span" animation="border" size="sm" />
                      ) : (
                        <img
                          src="https://www.google.com/favicon.ico"
                          width={16}
                          height={16}
                          alt=""
                          aria-hidden
                        />
                      )}
                      Google
                    </button>
                    <button
                      type="button"
                      className="lm-social-btn btn d-flex align-items-center justify-content-center gap-2 flex-fill d-none"
                      onClick={() => handleSocialLogin('facebook')}
                      disabled={!!socialLoading}
                      aria-hidden
                    >
                      {socialLoading === 'facebook' ? (
                        <Spinner as="span" animation="border" size="sm" />
                      ) : (
                        <img
                          src="https://www.facebook.com/favicon.ico"
                          width={16}
                          height={16}
                          alt=""
                          aria-hidden
                        />
                      )}
                      Facebook
                    </button>
                  </div>
                  <p className="text-center small text-body-secondary mb-0">
                    Non hai un account?{' '}
                    <button
                      type="button"
                      className="lm-switch-link"
                      onClick={goToRegister}
                    >
                      Registrati
                    </button>
                  </p>
                </>
              )}

              {/* ── FORGOT PASSWORD ── */}
              {view === 'forgot-password' && (
                <>
                  {forgotError && (
                    <Alert
                      variant="danger"
                      dismissible
                      onClose={() => setForgotError('')}
                      className="small"
                    >
                      {forgotError}
                    </Alert>
                  )}
                  <p className="small text-body-secondary mb-3">
                    Inserisci l'email associata al tuo account e ti invieremo un
                    link per reimpostare la password. Il link sarà valido per 60
                    minuti.
                  </p>
                  <Form onSubmit={handleForgotSubmit} noValidate>
                    <Form.Label
                      className="small fw-semibold"
                      htmlFor="forgot-email"
                    >
                      Email
                    </Form.Label>
                    <div className="lm-input-wrap">
                      <span className="lm-input-icon">
                        <Envelope size={16} />
                      </span>
                      <Form.Control
                        id="forgot-email"
                        type="email"
                        placeholder="latua@email.it"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        autoComplete="email"
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      className="lm-btn-primary mt-2"
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Invio in corso...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2" />
                          Inviami il link
                        </>
                      )}
                    </button>
                  </Form>
                  <p className="text-center small text-body-secondary mt-3 mb-0">
                    Ricordi la password?{' '}
                    <button
                      type="button"
                      className="lm-switch-link"
                      onClick={goToLogin}
                    >
                      Torna al login
                    </button>
                  </p>
                </>
              )}

              {/* ── FORGOT SENT ── */}
              {view === 'forgot-sent' && (
                <div className="text-center py-3">
                  <div className="fs-1 mb-3">📬</div>
                  <h5 className="fw-bold mb-2">Controlla la tua email</h5>
                  <p
                    className="small text-body-secondary mx-auto mb-4"
                    style={{ maxWidth: 320 }}
                  >
                    Se <strong>{forgotEmail}</strong> è associata a un account,
                    riceverai il link per reimpostare la password entro pochi
                    minuti.
                  </p>
                  <button
                    type="button"
                    className="lm-btn-primary d-block mx-auto mb-3"
                    style={{ maxWidth: 220 }}
                    onClick={onHide}
                  >
                    Ho capito
                  </button>
                  <p className="text-center small text-body-secondary mb-0">
                    Non hai ricevuto nulla?{' '}
                    <button
                      type="button"
                      className="lm-switch-link"
                      onClick={() => setView('forgot-password')}
                    >
                      Riprova
                    </button>
                  </p>
                </div>
              )}
            </div>

            {/* ══ BACK — register / confirm-email ══ */}
            <div className="lm-flip-face back">
              {view === 'confirm-email' ? (
                <div className="text-center py-3">
                  <div className="lm-confirm-icon mx-auto mb-4">📧</div>
                  <h5 className="fw-bold mb-2">Email inviata!</h5>
                  <p
                    className="small text-body-secondary mx-auto mb-4"
                    style={{ maxWidth: 300 }}
                  >
                    Abbiamo inviato un link di conferma a{' '}
                    <strong>{regEmail}</strong>. Controlla la tua casella e
                    clicca sul link per attivare l'account.
                  </p>
                  <button
                    type="button"
                    className="lm-btn-primary d-block mx-auto"
                    style={{ maxWidth: 220 }}
                    onClick={onHide}
                  >
                    Chiudi
                  </button>
                  <p className="text-center small text-body-secondary mt-3 mb-0">
                    Email sbagliata?{' '}
                    <button
                      type="button"
                      className="lm-switch-link"
                      onClick={() => setView('register')}
                    >
                      Torna indietro
                    </button>
                  </p>
                </div>
              ) : (
                <>
                  {regError && (
                    <Alert
                      variant="danger"
                      dismissible
                      onClose={() => setRegError('')}
                      className="small"
                    >
                      {regError}
                    </Alert>
                  )}
                  <Form onSubmit={handleRegisterSubmit} noValidate>
                    <Form.Label
                      className="small fw-semibold"
                      htmlFor="reg-name"
                    >
                      Nome e Cognome
                    </Form.Label>
                    <div className="lm-input-wrap">
                      <span className="lm-input-icon">
                        <PersonFill size={16} />
                      </span>
                      <Form.Control
                        id="reg-name"
                        type="text"
                        placeholder="Mario Rossi"
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        autoComplete="name"
                      />
                    </div>
                    <Form.Label className="small fw-semibold" htmlFor="reg-dob">
                      Data di nascita
                    </Form.Label>
                    <div className="lm-input-wrap">
                      <span className="lm-input-icon">
                        <CalendarFill size={16} />
                      </span>
                      <Form.Control
                        id="reg-dob"
                        type="date"
                        value={regDob}
                        onChange={(e) => setRegDob(e.target.value)}
                        autoComplete="bday"
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <Form.Label
                      className="small fw-semibold"
                      htmlFor="reg-email"
                    >
                      Email
                    </Form.Label>
                    <div className="lm-input-wrap">
                      <span className="lm-input-icon">
                        <Envelope size={16} />
                      </span>
                      <Form.Control
                        id="reg-email"
                        type="email"
                        placeholder="latua@email.it"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        autoComplete="email"
                        readOnly={!!lockEmail}
                        disabled={!!lockEmail}
                      />
                    </div>
                    <Form.Label
                      className="small fw-semibold"
                      htmlFor="reg-password"
                    >
                      Password
                    </Form.Label>
                    <div className="lm-input-wrap">
                      <span className="lm-input-icon">
                        <Lock size={16} />
                      </span>
                      <Form.Control
                        id="reg-password"
                        type={showRegPwd ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        onBlur={() => {
                          if (regPassword) setPwdBlurred(true);
                        }}
                        autoComplete="new-password"
                        aria-describedby="pwdStrength pwdHints"
                        className={
                          pwdBlurred &&
                          regPassword &&
                          !isPasswordValid(pwdValidation)
                            ? 'is-invalid'
                            : ''
                        }
                      />
                      <button
                        type="button"
                        className="lm-toggle"
                        onClick={() => setShowRegPwd((v) => !v)}
                        aria-label="Mostra/nascondi password"
                      >
                        {showRegPwd ? (
                          <EyeSlash size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                    {regPassword && (
                      <div id="pwdStrength">
                        <StrengthBar validation={pwdValidation} />
                      </div>
                    )}
                    {showPwdHints && (
                      <div
                        id="pwdHints"
                        className="lm-hints p-3 mb-3"
                        role="status"
                        aria-live="polite"
                      >
                        <p className="lm-hints-title mb-2">
                          La password deve contenere:
                        </p>
                        <ValidationItem
                          valid={pwdValidation.hasMinLength}
                          text="Almeno 8 caratteri"
                        />
                        <ValidationItem
                          valid={pwdValidation.hasUpperCase}
                          text="Una lettera maiuscola"
                        />
                        <ValidationItem
                          valid={pwdValidation.hasLowerCase}
                          text="Una lettera minuscola"
                        />
                        <ValidationItem
                          valid={pwdValidation.hasNumber}
                          text="Un numero"
                        />
                        <ValidationItem
                          valid={pwdValidation.hasSymbol}
                          text="Un simbolo (!@#$%...)"
                        />
                      </div>
                    )}
                    <Form.Label
                      className="small fw-semibold"
                      htmlFor="reg-confirm"
                    >
                      Conferma password
                    </Form.Label>
                    <div className="lm-input-wrap">
                      <span className="lm-input-icon">
                        <Lock size={16} />
                      </span>
                      <Form.Control
                        id="reg-confirm"
                        type={showRegConfirm ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={regConfirm}
                        onChange={(e) => setRegConfirm(e.target.value)}
                        onBlur={() => {
                          if (regConfirm) setConfirmBlurred(true);
                        }}
                        autoComplete="new-password"
                        className={showConfirmError ? 'is-invalid' : ''}
                      />
                      <button
                        type="button"
                        className="lm-toggle"
                        onClick={() => setShowRegConfirm((v) => !v)}
                        aria-label="Mostra/nascondi conferma password"
                      >
                        {showRegConfirm ? (
                          <EyeSlash size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                    {showConfirmError && (
                      <p className="lm-confirm-error mt-n2 mb-2">
                        Le password non coincidono.
                      </p>
                    )}
                    <button
                      type="submit"
                      className="lm-btn-primary mt-2"
                      disabled={regLoading}
                    >
                      {regLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Registrazione in corso...
                        </>
                      ) : (
                        'Crea account'
                      )}
                    </button>
                  </Form>
                  <div className="lm-divider my-3">oppure registrati con</div>
                  {socialError && (
                    <Alert
                      variant="danger"
                      dismissible
                      onClose={() => setSocialError('')}
                      className="small"
                    >
                      {socialError}
                    </Alert>
                  )}
                  <div className="d-flex gap-2 mb-3">
                    <button
                      type="button"
                      className="lm-social-btn btn d-flex align-items-center justify-content-center gap-2 flex-fill"
                      onClick={() => handleSocialLogin('google')}
                      disabled={!!socialLoading || regLoading}
                    >
                      {socialLoading === 'google' ? (
                        <Spinner as="span" animation="border" size="sm" />
                      ) : (
                        <img
                          src="https://www.google.com/favicon.ico"
                          width={16}
                          height={16}
                          alt=""
                          aria-hidden
                        />
                      )}
                      Google
                    </button>
                    <button
                      type="button"
                      className="lm-social-btn btn d-flex align-items-center justify-content-center gap-2 flex-fill d-none"
                      onClick={() => handleSocialLogin('facebook')}
                      disabled={!!socialLoading || regLoading}
                      aria-hidden
                    >
                      {socialLoading === 'facebook' ? (
                        <Spinner as="span" animation="border" size="sm" />
                      ) : (
                        <img
                          src="https://www.facebook.com/favicon.ico"
                          width={16}
                          height={16}
                          alt=""
                          aria-hidden
                        />
                      )}
                      Facebook
                    </button>
                  </div>
                  <p className="text-center small text-body-secondary mt-3 mb-0">
                    Hai già un account?{' '}
                    <button
                      type="button"
                      className="lm-switch-link"
                      onClick={goToLogin}
                    >
                      Vai alla login
                    </button>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LoginModal;
